/**
 * Pure H OS · Phase D Wave 4 · Unit Economics.
 * Per-SKU revenue, COGS, margin, CAC, CLTV, contribution.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { UNIT_ECONOMICS } from "../mocks/unitEconomicsData";

function marginSeverity(pct: number): "low" | "medium" | "high" {
  if (pct >= 65) return "low";
  if (pct >= 58) return "medium";
  return "high";
}

type Tab = "all" | "healthy" | "watch";

export default function UnitEconomics() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (tab === "all") return UNIT_ECONOMICS;
    if (tab === "healthy") return UNIT_ECONOMICS.filter((u) => u.marginPct >= 62);
    return UNIT_ECONOMICS.filter((u) => u.marginPct < 62);
  }, [tab]);

  const current = openId ? UNIT_ECONOMICS.find((u) => u.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="unit-econ-page">
      <Card testId="ue-filters">
        <div className="flex items-center justify-between mb-3">
          <SectionHeader eyebrow="Unit Economics" title="Per-SKU margin index" />
          <Link
            to="/pureh/finance/pnl"
            className="text-xs"
            style={{ color: "var(--ph-accent)" }}
            data-testid="ue-link-pnl"
          >
            View consolidated P&L →
          </Link>
        </div>
        <TabBar<Tab>
          testId="ue-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",     label: `All ${UNIT_ECONOMICS.length}` },
            { key: "healthy", label: "Margin ≥ 62%" },
            { key: "watch",   label: "Watch" },
          ]}
        />
      </Card>

      <Card padded={false} testId="ue-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Unit Economics" title={`${filtered.length} SKU${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>Revenue → margin → contribution per unit, per SKU.</div>
        </div>
        <div className="p-2">
          {filtered.map((u) => (
            <ListRow key={u.id} testId={`ue-${u.id}`}
              onClick={() => setOpenId(u.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{u.sku}</span>
                  <span>{u.name}</span>
                </span>
              }
              sublabel={<>EGP {u.revenuePerUnit.toLocaleString()} rev · {u.marginPct.toFixed(1)}% margin · EGP {u.contributionMargin.toLocaleString()} contribution · CLTV {u.cltv.toLocaleString()}</>}
              trailing={<SeverityBadge severity={marginSeverity(u.marginPct)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="ue-drawer" title={current ? current.name : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`SKU · ${current.sku}`} title={`${current.marginPct.toFixed(1)}% margin`} />
            <div className="grid grid-cols-2 gap-4">
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Revenue per unit</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.revenuePerUnit.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>COGS</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.cogs.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Gross profit</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.grossProfit.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Ad spend / unit</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.adSpendPerUnit.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>CAC</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.cac.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>CLTV</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.cltv.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Contribution</div><div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.contributionMargin.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Margin</div><div className="mt-2"><SeverityBadge severity={marginSeverity(current.marginPct)} /></div></div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
